/**
 * PracticingTabContent Component
 * Content for the Practicing tab in the profile detail view
 * Shows rituals user is organizing but didn't create
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { RitualCard } from "@/components/cards/ritualCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { formatRitualType } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { useToggleRitualSave } from "../hooks/useSavedRituals"
import { useUserRitualPractices } from "../hooks/useUserRitualPractices"

interface PracticingTabContentProps {
  userId: string
  className?: string
  searchQuery?: string
  isGuest?: boolean
}

export function PracticingTabContent({
  userId,
  className,
  searchQuery,
  isGuest = false,
}: PracticingTabContentProps) {
  const { t } = useTranslation("profile")
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const [showLoginModal, setShowLoginModal] = useState(false)
  const debouncedSearch = useDebounce(localSearch || searchQuery || "", 500)

  // Toggle save hook
  const toggleRitualSave = useToggleRitualSave()

  const { data, isLoading, isError } = useUserRitualPractices(userId, {
    limit: 50,
    search: debouncedSearch || undefined,
  })

  // Transform API data to match RitualCardProps
  const rituals =
    data?.rituals.map(ritual => ({
      id: ritual.id,
      image: ritual.uploads?.find(u => u.isCover)?.filePath || undefined,
      userAvatar: ritual.createdBy?.coverImage || undefined,
      username: ritual.createdBy?.name || t("practicing.unknown"),
      userHandle: ritual.createdBy?.username ? `@${ritual.createdBy.username}` : "",
      title: ritual.title,
      tags: [formatRitualType(ritual.type)],
      duration: formatDuration(ritual.duration),
      contextualTag: formatRitualType(ritual.type),
      userId: ritual.createdBy?.id,
      isBookmarked: ritual.isSaved,
      onBookmarkClick: () => toggleRitualSave.mutate(ritual.id),
      price: ritual.price ?? undefined,
      participationFee: ritual.participationFee,
    })) || []

  return (
    <div className={className}>
      {/* Loading State */}
      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-stone">{t("practicing.loading")}</p>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-red">{t("practicing.error")}</p>
        </div>
      )}

      {/* Rituals Grid */}
      {!isLoading && !isError && (
        <div className="mt-0 px-5">
          <div className="flex justify-between items-center gap-2">
            <h1 className="text-[18px] leading-[30px] font-monteserat font-semibold uppercase">
              {t("practicing.title")}
            </h1>
            <div className="lg:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label="Close search"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => (isGuest ? setShowLoginModal(true) : setShowSearch(true))}
                  aria-label="Search"
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>
          <p className="mt-2.5 md:mt-3 text-[14px] font-regular leading-[25px]">
            {t("practicing.description")}
          </p>
          {rituals.length === 0 ? (
            <div className="h-[250px] mt-4">
              <p className="text-stone mt-3 text-[14px] font-regular leading-[25px]">
                {t("practicing.notFound")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-5 md:my-10">
              {rituals.map(ritual => (
                <div key={ritual.id} className="h-[260px] md:h-[500px]">
                  <RitualCard {...ritual} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
